import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Modal, Radio } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';

import { AvailableModeRepo, getArtifactModelSizeBytes } from '@/components-biz/AvailableModeRepo';
import { ModelPlazaRepoSelector, getModelPlazaArtifactSize } from '@/components-biz/ModelPlazaRepoSelector';
import { ModelSelector } from '@/components/ModelScopeSelector';
import { MAAS_INPUT_PLACEHOLDER, maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { DEFAULT_MAAS_OPTIONS, MaasModelSourceType, type MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { fetchAndApplyModelConfigResource } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelConfigResource';
import { bytesToGi, toK8sGiQuantity } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';

import { ModelAddCard, ModelInfoCard } from './model-info-card';
import styles from './model-source-setting.module.less';
import { useModelSourceSetting } from './useModelSourceSetting';

const FormItem = Form.Item;

const MODAL_STYLE = { width: '60vw', minWidth: 700, maxWidth: 1000 };

interface ModelSourceSettingProps {
  form: FormInstance;
  typeOptions?: MaasOptions;
  lockedSourceType?: MaasModelSourceType;
}

/** 按来源类型返回需校验的表单字段 */
function getModelSourceValidateFields(sourceType: MaasModelSourceType): string[] {
  const fields = ['model.size'];
  switch (sourceType) {
    case MaasModelSourceType.ModelScope:
      return [...fields, 'model.source.modelscope.model'];
    case MaasModelSourceType.HuggingFace:
      return [...fields, 'model.source.huggingface.model'];
    case MaasModelSourceType.StackScope:
      return [...fields, 'model.source.stackscope.model', 'model.source.stackscope.url'];
    case MaasModelSourceType.ModelPlaza:
      return [...fields, 'model.source.modelplaza.model', 'model.source.modelplaza.url'];
    default:
      return fields;
  }
}

interface ModelSourceModalProps {
  visible: boolean;
  form: FormInstance;
  sourceType: MaasModelSourceType;
  sourceOptions: { label: string; value: string }[];
  modalSession: number;
  onSourceTypeChange: (type: MaasModelSourceType) => void;
  sourceTypeLocked?: boolean;
  onOk: () => void;
  onCancel: () => void;
}

function ModelSourceModal({
  visible,
  form,
  sourceType,
  sourceOptions,
  modalSession,
  onSourceTypeChange,
  sourceTypeLocked,
  onOk,
  onCancel,
}: ModelSourceModalProps) {
  const { t } = useTranslation();
  const modelscopeModel = Form.useWatch('model.source.modelscope.model', form) as string | undefined;
  const stackscopeSource = Form.useWatch('model.source.stackscope', form) as { model?: string; url?: string } | undefined;
  const modelplazaSource = Form.useWatch('model.source.modelplaza', form) as { model?: string; url?: string } | undefined;
  const modelSize = Form.useWatch('model.size', form) as string | undefined;
  const modelSizeAutoFilled = sourceType === MaasModelSourceType.ModelScope && !!toK8sGiQuantity(modelSize);

  const renderModelSizeField = () => (
    <FormItem
      label={t('data-center:maas.model.size')}
      field="model.size"
      rules={maasRequiredRules(t('data-center:maas.model.size'))}
      extra={modelSizeAutoFilled ? undefined : t('data-center:maas.model.size.required')}
    >
      <Input placeholder="10Gi" disabled={modelSizeAutoFilled} />
    </FormItem>
  );

  const renderSourceDetail = () => {
    switch (sourceType) {
      case MaasModelSourceType.ModelScope:
        return (
          <>
            <FormItem field="model.source.modelscope.model" rules={maasRequiredRules(t('data-center:maas.model.source.modelscope'))} hidden>
              <Input />
            </FormItem>
            <FormItem label={t('data-center:maas.model.source.modelscope')} required>
              <ModelSelector
                key={`modelscope-${modalSession}`}
                value={modelscopeModel}
                onChange={(value, model) => {
                  form.setFieldValue('model.source.modelscope.model', value);
                  if (model?.Name) {
                    form.setFieldValue('model.name', model.Name);
                  }
                  const sizeBytes = Number(model?.StorageSize);
                  if (sizeBytes > 0) {
                    form.setFieldValue('model.size', toK8sGiQuantity(bytesToGi(sizeBytes)));
                  } else {
                    form.setFieldValue('model.size', undefined);
                  }
                  fetchAndApplyModelConfigResource(form, MaasModelSourceType.ModelScope, value).catch(() => undefined);
                }}
              />
            </FormItem>
          </>
        );
      case MaasModelSourceType.HuggingFace:
        return (
          <FormItem
            label={t('data-center:maas.model.source.huggingface')}
            field="model.source.huggingface.model"
            rules={maasRequiredRules(t('data-center:maas.model.source.huggingface'))}
          >
            <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
          </FormItem>
        );
      case MaasModelSourceType.StackScope:
        return (
          <>
            <FormItem field="model.source.stackscope.model" rules={maasRequiredRules(t('hooks:model.repository'))} hidden>
              <Input />
            </FormItem>
            <FormItem field="model.source.stackscope.url" rules={maasRequiredRules(t('hooks:model.repository'))} hidden>
              <Input />
            </FormItem>
            <FormItem label={t('hooks:model.repository')} required>
              <AvailableModeRepo
                key={`stackscope-${modalSession}`}
                value={stackscopeSource}
                onChange={(source, item) => {
                  form.setFieldValue('model.source.stackscope', source);
                  if (item.name) {
                    form.setFieldValue('model.name', item.name);
                  }
                  const sizeBytes = getArtifactModelSizeBytes(item);
                  form.setFieldValue('model.size', sizeBytes);
                }}
              />
            </FormItem>
          </>
        );
      case MaasModelSourceType.ModelPlaza:
        return (
          <>
            <FormItem field="model.source.modelplaza.model" rules={maasRequiredRules(t('home:market.model.repository'))} hidden>
              <Input />
            </FormItem>
            <FormItem field="model.source.modelplaza.url" rules={maasRequiredRules(t('home:market.model.repository'))} hidden>
              <Input />
            </FormItem>
            <FormItem label={t('home:market.model.repository')} required>
              <ModelPlazaRepoSelector
                key={`modelplaza-${modalSession}`}
                value={modelplazaSource}
                onChange={(source, item) => {
                  form.setFieldValue('model.source.modelplaza', source);
                  if (item.name) {
                    form.setFieldValue('model.name', item.name);
                  }
                  const sizeBytes = getModelPlazaArtifactSize(item);
                  form.setFieldValue('model.size', sizeBytes);
                }}
              />
            </FormItem>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      title={t('data-center:maas.model.select')}
      onOk={onOk}
      onCancel={onCancel}
      style={MODAL_STYLE}
      maskClosable={false}
      unmountOnExit
    >
      <div className={styles.modalForm}>
        <FormItem label={t('data-center:maas.model.source')} required className={styles.sourceTypeRow} layout="horizontal">
          <Radio.Group
            options={sourceOptions}
            type="button"
            size="small"
            value={sourceType}
            disabled={sourceTypeLocked}
            onChange={onSourceTypeChange}
          />
        </FormItem>
        {renderModelSizeField()}
        {renderSourceDetail()}
      </div>
    </Modal>
  );
}

/** 模型来源：添加/展示卡片 + 弹窗配置 */
export function ModelSourceSetting({ form, typeOptions = DEFAULT_MAAS_OPTIONS, lockedSourceType }: ModelSourceSettingProps) {
  const { t } = useTranslation();
  const {
    modalVisible,
    modalSession,
    sourceType,
    sourceOptions,
    isConfigured,
    modelName,
    sourceSummary,
    sourceDetail,
    modelSizeLabel,
    handleSourceTypeChange,
    openModal,
    closeModal,
    sourceTypeLocked,
  } = useModelSourceSetting({
    form,
    typeOptions,
    t,
    lockedSourceType,
  });

  const handleModalOk = useCallback(async () => {
    const fields = getModelSourceValidateFields(sourceType);
    try {
      await form.validate(fields);
      if (sourceType === MaasModelSourceType.HuggingFace) {
        const modelId = String(form.getFieldValue('model.source.huggingface.model') || '').trim();
        if (modelId) {
          await fetchAndApplyModelConfigResource(form, MaasModelSourceType.HuggingFace, modelId).catch(() => undefined);
        }
      }
      closeModal();
      void form.validate(['model.source']);
    } catch {
      // 校验失败时保持弹窗打开
    }
  }, [closeModal, form, sourceType]);

  return (
    <div className={styles.modelSourceRoot}>
      <div className={styles.modelSourceBlock}>
        {isConfigured ? (
          <ModelInfoCard
            modelName={modelName}
            sourceSummary={sourceSummary}
            sourceDetail={sourceDetail}
            modelSize={modelSizeLabel}
            onEdit={openModal}
            showRequiredTag
          />
        ) : (
          <ModelAddCard onClick={openModal} />
        )}
        <Input className={styles.modelSourceHiddenInput} tabIndex={-1} aria-hidden />
      </div>

      <ModelSourceModal
        visible={modalVisible}
        form={form}
        sourceType={sourceType}
        sourceOptions={sourceOptions}
        modalSession={modalSession}
        onSourceTypeChange={handleSourceTypeChange}
        sourceTypeLocked={sourceTypeLocked}
        onOk={handleModalOk}
        onCancel={closeModal}
      />
    </div>
  );
}

export default ModelSourceSetting;
